import { usePostHog } from "@posthog/react";
import { ColumnDef } from "@tanstack/react-table";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { Button } from "components/ui/button";
import { Dataset, DatasetList } from "data/models";
import { fetchDatasetsLink } from "data/queries-common";
import { CircleCheck, CircleX, Download } from "lucide-react";
import { formatProductName } from "pages/geoscape-admin/manage-release/new-release/utils";
import { formatDate } from "utils";
import { formatBytes } from "utils/commons";

type Dictionary = {
  [key: string]: string;
};

const DisplayFormatMapping: Dictionary = {
  GDB: "GDB",
  GEOJSON: "GeoJSON",
  SHP: "Shapefile",
  TAB: "MapInfo TAB",
  LYNX: "LYNX",
  PUMP: "PUMP",
  GEOTIFF: "GEOTIFF",
  PSV: "Pipe-Separated Values",
};

const DisplayStateMapping: Dictionary = {
  "ALL STATES": "All States",
  AUSTRALIA: "Australia",
};

type DatasetRow = {
  id: string;
  name: string;
  parentProduct?: string;
  product: string;
  insight: string;
  region: string;
  format: string;
  size: string | number;
  release: string;
  publishDate: string;
  licenced: boolean;
  datum: string;
};

const customColumns = (): ColumnDef<DatasetRow>[] => {
  const { enqueueLinkCorruptedSnackbar } = useCustomSnackbars();
  const posthog = usePostHog();
  return [
    {
      accessorKey: "product",
      header: "Product",
      enableSorting: true,
      // `product` is mapped through formatProductName; `name` is the raw API value, which
      // still carries pre-rename product names.
      cell: ({ row }) => row.original.product,
    },
    {
      accessorKey: "insight",
      header: "Insight Pack",
      enableSorting: true,
    },
    {
      accessorKey: "release",
      header: "Release",
      size: 90,
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        const [year, month] = val.split("-").map(Number);
        const monthName = new Date(year, month - 1).toLocaleString("default", {
          month: "long",
        });
        return `${monthName} ${year}`;
      },
    },
    {
      accessorKey: "region",
      header: "Region",
      enableSorting: true,
      size: 70,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return DisplayStateMapping[val] ?? val;
      },
    },
    {
      accessorKey: "format",
      header: "Format",
      size: 90,
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return DisplayFormatMapping[val] ?? val;
      },
    },
    {
      accessorKey: "datum",
      header: "Datum",
      size: 70,
      enableSorting: true,
    },
    {
      accessorKey: "size",
      header: "File Size",
      size: 60,
      enableSorting: true,
      cell: ({ getValue }) => formatBytes(getValue() as number),
    },
    {
      accessorKey: "publishDate",
      header: "Publish Date",
      size: 70,
      enableSorting: true,
      cell: ({ getValue }) => formatDate(getValue() as string),
    },
    {
      accessorKey: "licenced",
      header: "Licenced",
      size: 60,
      enableSorting: true,
      cell: ({ getValue }) =>
        getValue() ? (
          <div className="flex w-full h-full items-center justify-center">
            <CircleCheck className="text-success size-4" />
          </div>
        ) : (
          <div className="flex w-full h-full items-center justify-center">
            <CircleX className="text-destructive size-4" />
          </div>
        ),
    },
    {
      accessorKey: "id",
      enableSorting: false,
      size: 48,
      header: "",
      cell: ({ row }) => {
        return (
          <div className="flex items-center justify-end">
            {row.original.licenced ? (
              <Button
                variant="outline"
                size="icon-lg"
                onClick={async () => {
                  try {
                    const response = await fetchDatasetsLink(row.original.id);

                    const a = document.createElement("a");
                    a.href = response;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(response);
                    posthog.capture("user_downloaded_dataset", {
                      id: row.original.id,
                      name: row.original.name,
                      region: row.original.region,
                      release: row.original.release,
                      datum: row.original.datum,
                      format: row.original.format,
                    });
                  } catch (er) {
                    enqueueLinkCorruptedSnackbar();
                  }
                }}
              >
                <Download className="text-primary" />
              </Button>
            ) : (
              <Button variant="ghost" size="icon-lg" disabled>
                <Download className="text-muted-foreground" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];
};

const customersToRows = (datasetsList: DatasetList): DatasetRow[] => {
  const rows = datasetsList.datasets.map((key: Dataset) => {
    return {
      id: key.id,
      name: key.name,
      parentProduct: key.parentProduct,
      product: !!key.parentProduct
        ? formatProductName(key.parentProduct)
        : formatProductName(key.product),
      insight: key.parentProduct ? formatProductName(key.product) : "-",
      region: key.region,
      format: key.format,
      size: key.size,
      release: key.release,
      publishDate: key.publishDate,
      licenced: key.licenced,
      datum: key.datum,
    };
  });
  return rows;
};

export { customColumns, customersToRows };
export type { DatasetRow };
